/**
 * CurrenciesProvider
 * Contain most logic handing with Omnitech Api
 */

import React, { useContext, useState, useEffect, useCallback } from 'react'
import _ from 'lodash'

import { CacheChecksumContext, createPersistedState } from '../../cache'
import CurrenciesContext from './currencies-context'
import { fetchCurrencies } from './currencies-api'
import { cancelRequest } from '../../helpers'

// create use state hook with persisted storage
// TODO: support using different storage provider
const useCurrenciesState = createPersistedState('currencies')
const useCurrenciesChecksumState = createPersistedState('currenciesChecksum')

export default function CurrenciesProvider({ children }) {
  // handle currencies cache checksum value
  const { getChecksum } = useContext(CacheChecksumContext)
  const cacheChecksumValue = getChecksum('currencies')

  // manage state for currencies and checksum
  const [currencies, setCurrencies] = useCurrenciesState({})
  const [currenciesChecksum, setCurrenciesChecksum] = useCurrenciesChecksumState('')
  const [currenciesReady, setCurrenciesReady] = useState(false)
  const [enableRefresh, setEnableRefresh] = useState(false)

  const getCurrencies = useCallback(async () => {
    try {
      const { cacheChecksum, currencies } = await fetchCurrencies()
      setCurrencies(currencies)
      setCurrenciesReady(true)
      setEnableRefresh(true)
      setCurrenciesChecksum(cacheChecksum)
    } catch (error) {
      // TODO: handle error
    }
  }, [fetchCurrencies])

  const updateCache = useCallback(async () => {
    try {
      setEnableRefresh(false)
      const { cacheChecksum, currencies } = await fetchCurrencies()
      setCurrencies(currencies)
      setCurrenciesChecksum(cacheChecksum)
    } catch (error) {
      // TODO: handle error
    } finally {
      setEnableRefresh(true)
    }
  }, [fetchCurrencies, setCurrencies, setCurrenciesChecksum, setEnableRefresh])

  /**
   * get currencies from omnitech API when app is mounted
   */
  useEffect(() => {
    // use currencies in cache first
    if (!_.isEmpty(currencies)) {
      setCurrenciesReady(true)
      setEnableRefresh(true)
      return
    }

    getCurrencies()

    return function cleanUp() {
      cancelRequest.cancelAll([
        'fetchCurrencies'
      ])
    }
  }, [])

  // update currencies cache base on cache checksum
  useEffect(() => {
    if (!currenciesReady || _.isUndefined(cacheChecksumValue)) return

    // when it is not required to update cache, skip the process
    if (!enableRefresh || currenciesChecksum === cacheChecksumValue) {
      if (_.isEmpty(currenciesChecksum) && !_.isEmpty(cacheChecksumValue)) {
        setCurrenciesChecksum(cacheChecksumValue)
      }
      return
    }

    // start refreshing currencies with omnitech API
    updateCache(cacheChecksumValue)

    return function cleanUp() {
      cancelRequest.cancelAll([
        'fetchCurrencies'
      ])
    }
  }, [currenciesReady, currenciesChecksum, cacheChecksumValue, updateCache])

  const state = {
    currencies,
    currenciesReady
  }
  return (
    <CurrenciesContext.Provider value={state}>
      {children}
    </CurrenciesContext.Provider>
  )
}
